/**
 * Frontend Rankings Display
 * Public-facing player leaderboard with expandable tournament history
 */

import { __, _n, sprintf, htmlLang } from '../lib/i18n.js';

class FrontendRankings {
    constructor() {
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initializeAll());
        } else {
            this.initializeAll();
        }
    }

    initializeAll() {
        const containers = document.querySelectorAll('.big-squash-rankings-container');
        containers.forEach(container => {
            const inactiveDays = parseInt(container.dataset.inactiveDays) || 180;
            this.loadRankings(container, inactiveDays);
        });
    }

    async loadRankings(container, inactiveDays) {
        const { restUrl, restNonce, currentUserId, playerProfileBase } = window.bigSquashRankings || {};
        if (!restUrl) {
            container.innerHTML = `<div class="bsr-error">${this.esc(__('Rankings not available'))}</div>`;
            return;
        }

        this.playerProfileBase = playerProfileBase || '';

        try {
            const response = await fetch(`${restUrl}/ratings?limit=200`, {
                headers: { 'X-WP-Nonce': restNonce }
            });
            const data = await response.json();

            if (!Array.isArray(data) || data.length === 0) {
                container.innerHTML = `<div class="bsr-empty">${this.esc(__('No rankings available yet.'))}</div>`;
                return;
            }

            container.innerHTML = this.renderLeaderboard(data, inactiveDays, currentUserId);
            this.bindEvents(container);
        } catch (error) {
            container.innerHTML = `<div class="bsr-error">${this.esc(__('Failed to load rankings.'))}</div>`;
        }
    }

    renderLeaderboard(players, inactiveDays, currentUserId) {
        const inactiveCount = players.filter(p => p.days_inactive >= inactiveDays).length;

        let html = '<div class="bsr-leaderboard">';

        html += '<table class="bsr-table">';
        html += `<thead><tr>
            <th class="bsr-col-rank">#</th>
            <th class="bsr-col-player">${this.esc(__('Player'))}</th>
            <th class="bsr-col-rating">${this.esc(__('Rating'))}</th>
            <th class="bsr-col-matches hide-mobile">${this.esc(__('Played'))}</th>
            <th class="bsr-col-trend hide-mobile">${this.esc(__('Trend'))}</th>
            <th class="bsr-col-toggle hide-mobile" aria-hidden="true"></th>
        </tr></thead>`;
        html += '<tbody>';

        // Active players get sequential ranks; inactive get their own sequence
        let activeRank = 0;
        let inactiveRank = 0;
        players.forEach((player) => {
            const isInactive = player.days_inactive >= inactiveDays;
            if (isInactive) {
                inactiveRank++;
                html += this.renderPlayerRow(player, inactiveRank, currentUserId, isInactive);
            } else {
                activeRank++;
                html += this.renderPlayerRow(player, activeRank, currentUserId, isInactive);
            }
        });

        html += '</tbody></table>';

        if (inactiveCount > 0) {
            html += `<button class="bsr-toggle-inactive" data-showing="false">${this.esc(sprintf(__('Include inactive players (%d)'), inactiveCount))}</button>`;
        }

        html += '</div>';
        return html;
    }

    renderPlayerRow(player, rank, currentUserId, isInactive) {
        const isCurrentUser = currentUserId && player.wp_user_id === currentUserId;
        const classes = [
            'bsr-row',
            isInactive ? 'bsr-row--inactive' : '',
            isCurrentUser ? 'bsr-row--current-user' : '',
        ].filter(Boolean).join(' ');

        const hiddenAttr = isInactive ? 'style="display:none;"' : '';
        const flag = `<span class="fi fi-${player.country.toLowerCase()}"></span>`;
        const confidence = this.renderConfidenceBars(player.rating_deviation);
        const trend = this.renderTrend(player);

        const rankDisplay = rank;

        let nameExtra = '';
        if (isInactive && player.last_active) {
            const date = new Date(player.last_active);
            const monthYear = date.toLocaleDateString(htmlLang(), { month: 'short', year: 'numeric' });
            nameExtra = `<span class="bsr-inactive-label">${this.esc(sprintf(__('Last played: %s'), monthYear))}</span>`;
        }

        const profileHref = player.slug && this.playerProfileBase
            ? `${this.playerProfileBase}${encodeURIComponent(player.slug)}`
            : '';
        const slugAttr = player.slug ? ` data-slug="${this.esc(player.slug)}"` : '';
        const nameInner = `${flag} <span class="bsr-name">${this.esc(player.name)}</span>${nameExtra}`;
        const nameCell = profileHref
            ? `<a class="bsr-player-link" href="${profileHref}">${nameInner}</a>`
            : nameInner;

        return `
            <tr class="${classes}" ${hiddenAttr} data-player-id="${player.player_id}" data-matches="${player.matches_played}"${slugAttr}>
                <td class="bsr-col-rank">${rankDisplay}</td>
                <td class="bsr-col-player">${nameCell}</td>
                <td class="bsr-col-rating"><span class="bsr-rating-value"><strong>${Math.round(player.rating)}</strong>${confidence}</span></td>
                <td class="bsr-col-matches hide-mobile">${player.matches_played}</td>
                <td class="bsr-col-trend hide-mobile">${trend}</td>
                <td class="bsr-col-toggle hide-mobile"><button type="button" class="bsr-trend-toggle" aria-expanded="false" aria-label="${this.esc(__('Show recent trend'))}"></button></td>
            </tr>
            <tr class="bsr-detail-row" style="display:none;" data-detail-for="${player.player_id}">
                <td colspan="6">
                    <div class="bsr-detail-content">${this.esc(__('Loading...'))}</div>
                </td>
            </tr>
        `;
    }

    renderConfidenceBars(rd) {
        let solidCount, barClass = 'bsr-bar--green';
        if (rd < 100) { solidCount = 3; }
        else if (rd < 150) { solidCount = 2; }
        else if (rd < 200) { solidCount = 1; barClass = 'bsr-bar--yellow'; }
        else { solidCount = 0; }

        let bars = '';
        for (let i = 0; i < 3; i++) {
            bars += `<span class="bsr-bar ${i < solidCount ? barClass : 'bsr-bar--empty'}"></span>`;
        }
        return `<span class="bsr-bars">${bars}</span>`;
    }

    renderTrend(player) {
        // We don't have last_change in the API yet, so show nothing for now
        // This will be populated when we add trend data
        return '';
    }

    bindEvents(container) {
        // Toggle inactive
        const toggleBtn = container.querySelector('.bsr-toggle-inactive');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', () => {
                const showing = toggleBtn.dataset.showing === 'true';
                const inactiveRows = container.querySelectorAll('.bsr-row--inactive');
                inactiveRows.forEach(row => {
                    row.style.display = showing ? 'none' : '';
                });
                toggleBtn.dataset.showing = showing ? 'false' : 'true';
                const count = inactiveRows.length;
                toggleBtn.textContent = showing
                    ? sprintf(__('Include inactive players (%d)'), count)
                    : sprintf(__('Exclude inactive players (%d)'), count);

                // Re-number visible ranks
                this.renumberRanks(container, !showing);
            });
        }

        // Row click navigates to the player's public profile.
        container.querySelectorAll('.bsr-row').forEach(row => {
            row.addEventListener('click', (event) => {
                if (event.target.closest('.bsr-trend-toggle, .bsr-player-link')) return;
                const slug = row.dataset.slug;
                if (!slug || !this.playerProfileBase) return;
                window.location.href = `${this.playerProfileBase}${encodeURIComponent(slug)}`;
            });
        });

        // Chevron toggles the inline trend detail.
        container.querySelectorAll('.bsr-trend-toggle').forEach(toggle => {
            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                const row = toggle.closest('.bsr-row');
                if (!row) return;
                const playerId = row.dataset.playerId;
                const detailRow = container.querySelector(`[data-detail-for="${playerId}"]`);
                if (!detailRow) return;

                const isOpen = detailRow.style.display !== 'none';

                // Close any other open detail rows first.
                container.querySelectorAll('.bsr-detail-row').forEach(r => {
                    if (r !== detailRow) r.style.display = 'none';
                });
                container.querySelectorAll('.bsr-row').forEach(r => {
                    if (r !== row) r.classList.remove('bsr-row--expanded');
                });
                container.querySelectorAll('.bsr-trend-toggle').forEach(t => {
                    if (t !== toggle) {
                        t.classList.remove('bsr-trend-toggle--open');
                        t.setAttribute('aria-expanded', 'false');
                    }
                });

                if (isOpen) {
                    detailRow.style.display = 'none';
                    row.classList.remove('bsr-row--expanded');
                    toggle.classList.remove('bsr-trend-toggle--open');
                    toggle.setAttribute('aria-expanded', 'false');
                } else {
                    detailRow.style.display = '';
                    row.classList.add('bsr-row--expanded');
                    toggle.classList.add('bsr-trend-toggle--open');
                    toggle.setAttribute('aria-expanded', 'true');
                    if (!detailRow.dataset.loaded) {
                        this.loadPlayerHistory(detailRow, playerId);
                    }
                }
            });
        });
    }

    async loadPlayerHistory(detailRow, playerId) {
        const { restUrl, restNonce } = window.bigSquashRankings || {};
        const contentDiv = detailRow.querySelector('.bsr-detail-content');
        const playerRow = detailRow.previousElementSibling;
        const matchesPlayed = playerRow ? parseInt(playerRow.dataset.matches, 10) : 0;

        try {
            const response = await fetch(`${restUrl}/ratings/player/${playerId}`, {
                headers: { 'X-WP-Nonce': restNonce }
            });
            const data = await response.json();

            // Mobile-only matches played line
            const matchesLine = matchesPlayed
                ? `<div class="bsr-detail-matches hide-desktop">${this.esc(sprintf(_n('%d match played', '%d matches played', matchesPlayed), matchesPlayed))}</div>`
                : '';

            if (data.history && data.history.length > 0) {
                // Group per-match entries by tournament, show last 3 tournaments
                const tournaments = this.groupByTournament(data.history);
                const recent = tournaments.slice(-3).reverse();
                contentDiv.innerHTML = `
                    ${matchesLine}
                    <div class="bsr-history-list">
                        ${recent.map(t => {
                            const change = t.ratingAfter - t.ratingBefore;
                            const changeClass = change >= 0 ? 'bsr-change--positive' : 'bsr-change--negative';
                            const prefix = change >= 0 ? '+' : '';
                            const date = t.date ? new Date(t.date).toLocaleDateString(htmlLang(), { month: 'short', year: 'numeric' }) : '';

                            return `
                                <div class="bsr-history-item">
                                    <span class="bsr-history-name">${this.esc(t.name)}</span>
                                    <span class="bsr-history-date">${this.esc(date)}</span>
                                    <span class="bsr-history-change ${changeClass}">${prefix}${Math.round(change)}</span>
                                </div>
                            `;
                        }).join('')}
                    </div>
                `;
            } else {
                contentDiv.innerHTML = `${matchesLine}<div class="bsr-history-empty">${this.esc(__('No tournament history'))}</div>`;
            }
            detailRow.dataset.loaded = 'true';
        } catch (error) {
            contentDiv.innerHTML = `<div class="bsr-history-empty">${this.esc(__('Failed to load history'))}</div>`;
        }
    }

    renumberRanks(container, showingInactive) {
        const rows = container.querySelectorAll('.bsr-row');
        let rank = 0;
        rows.forEach(row => {
            const isInactive = row.classList.contains('bsr-row--inactive');
            if (!showingInactive && isInactive) return;
            rank++;
            const rankCell = row.querySelector('.bsr-col-rank');
            if (rankCell) rankCell.textContent = rank;
        });
    }

    groupByTournament(history) {
        const map = new Map();

        for (const h of history) {
            const tid = h.tournament_id;
            if (!map.has(tid)) {
                map.set(tid, {
                    tournamentId: tid,
                    name: h.tournament_name,
                    date: h.tournament_date,
                    ratingBefore: parseFloat(h.rating_before),
                    ratingAfter: parseFloat(h.rating_after),
                    matchCount: 0,
                });
            }
            const t = map.get(tid);
            t.ratingAfter = parseFloat(h.rating_after);
            t.matchCount++;
        }

        return Array.from(map.values());
    }

    esc(text) {
        const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
        return String(text).replace(/[&<>"']/g, m => map[m]);
    }
}

new FrontendRankings();
